import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import parse from "html-react-parser";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import LoadingSpinner from "../../loader/Loader";
import { useGetWooProductsQuery } from "../../../features/api/wooApiSlice";
import { useUnlinkProductVideoMutation } from "../../../features/api/productApiSlice";

const WooProductsList = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    page: 1,
    per_page: 10,
  });
  const [unlinkProductVideo] = useUnlinkProductVideoMutation();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const {
    data: products = [],
    isLoading: productsLoading,
    isError,
    isFetching,
    refetch,
  } = useGetWooProductsQuery(filters);

  useEffect(() => {
    if (!products?.length && !isFetching && !isError) {
      refetch();
    }
  }, [products?.length, isFetching, isError, refetch]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    // ✅ Use filters.search, not filters
    const searchTerm = filters.search.toLowerCase();
    return products.filter((p) => {
      // ✅ Add a defensive check for p?.name before calling toLowerCase()
      const productName = p?.name ? p.name.toLowerCase() : "";
      return productName.includes(searchTerm);
    });
  }, [products, filters.search]); // ✅ Update dependency array

  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  const currentProducts = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredProducts.slice(startIdx, startIdx + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = (e) => {
    // ✅ Correctly update the search property of the filters object
    setFilters({ ...filters, search: e.target.value });
    setCurrentPage(1); // Reset to the first page on a new search
  };

  const handleUnlinkVideo = async (productId) => {
    try {
      await unlinkProductVideo({ productId: productId }).unwrap();
      toast.success("Product successfully unlinked from videos.");
    } catch (err) {
      console.error("Failed to unlink product:", err);
      toast.error(err?.data?.message || "Failed to unlink product.");
    }
  };

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Products</h1>
          <button onClick={() => navigate("/admin/products/add-product")}>
            Add Product
          </button>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by name"
          // ✅ Use filters.search for the value
          value={filters.search}
          // ✅ Use the new handler
          onChange={handleSearchChange}
        />
      </div>
      {/* ✅ Admin informational message */}
      <div className="admin-info-message">
        <p>
          Clicking the **Unlink Video** button will permanently remove the
          connection between the selected product and all associated videos.
          This action cannot be undone.
        </p>
      </div>
      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Image</th>
            <th>Name</th>
            <th>Price $</th>
            <th>In Stock</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {(productsLoading || isFetching) && <LoadingSpinner />}
          {isError && (
            <p className="error-message">
              Error fetching products. Please try again.
            </p>
          )}
          {currentProducts?.map((tx, index) => {
            const isLinked = tx?.linkedVideoCount > 0;
            const stockStatus = tx?.stock_status === "instock";

            // **NEW LOGIC: More robust price HTML cleaning**
            let cleanedPriceHtml = tx?.price_html;

            // **NEW LOGIC: Remove the screen-reader-text span**
            if (cleanedPriceHtml) {
              cleanedPriceHtml = cleanedPriceHtml.replace(
                /<span class="screen-reader-text">.*?<\/span>/g,
                ""
              );
            }

            return (
              <tr key={tx?.id || tx?._id}>
                <td>{index + 1}</td>
                <td>
                  <img
                    className="table-image"
                    src={tx.images?.[0]?.src}
                    alt={tx.name}
                  />
                </td>
                <td>{parse(`${tx?.name?.trim().substring(0, 40)}...`)}</td>
                <td>
                  <p dangerouslySetInnerHTML={{ __html: cleanedPriceHtml }} />
                </td>

                {/* ✅ Stock Status */}
                <td>
                  <span
                    style={{
                      color: stockStatus ? "green" : "red",
                      fontWeight: "bold",
                    }}
                  >
                    {stockStatus ? "In Stock" : "Out of Stock"}
                  </span>
                </td>

                {/* ✅ Actions */}
                <td className="actions-container">
                  <Link
                    className="attach-button"
                    aria-label={`attach video ${tx.name}`}
                    to={`/admin/videos/add-video/${tx?.id}`} // Using WooCommerce product id
                  >
                    Attach Video
                  </Link>
                  {isLinked && (
                    <button
                      className="unlink-button"
                      onClick={() => handleUnlinkVideo(tx?.id)} // Using WooCommerce product id
                    >
                      Unlink Video
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default WooProductsList;
